import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleCheck, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { userManagementService, type OrgUser } from '@/services/user-management.service';
import { PageHeader } from '@/components/shared/PageHeader';
import { SkeletonPage } from '@/components/skeletons';
import { EmptyState } from '@/components/shared/EmptyState';
import { Button } from '@/components/ui/button';

/** The identity fields D-074 made mandatory, in the order they are shown. */
const REQUIRED_FIELDS = [
  { key: 'username', label: 'Username' },
  { key: 'phone', label: 'Mobile' },
  { key: 'employeeCode', label: 'Employee Code' },
] as const;

function missingFields(user: OrgUser): string[] {
  return REQUIRED_FIELDS.filter((f) => {
    const value = user[f.key];
    return value === null || value === undefined || value.trim() === '';
  }).map((f) => f.label);
}

/**
 * Lists employees whose identity predates D-074, when username, mobile and
 * employee code became mandatory.
 *
 * Those accounts were deliberately grandfathered rather than backfilled with
 * invented data — they still sign in normally, but any edit now requires the
 * full identity. This page exists so that debt is visible and actionable
 * instead of surfacing as a confusing validation error the first time someone
 * opens an old employee record.
 *
 * The list comes from the existing `GET /users` endpoint via its
 * `incompleteIdentity` flag — same permission, same tenant scoping, same
 * pagination. No parallel query path.
 */
export function IncompleteIdentityPage() {
  const { data, isLoading } = useQuery({
    queryKey: ['users', 'incomplete-identity'],
    queryFn: () => userManagementService.list({ incompleteIdentity: true, pageSize: 100, page: 1 }),
  });

  // Denominator for the progress bar. The filtered query cannot supply it — it
  // only ever returns incomplete rows — so the tenant's total headcount is
  // fetched separately. `total` comes from the same paginated envelope, so this
  // costs one extra request and no extra rows.
  const { data: allUsers } = useQuery({
    queryKey: ['users', 'identity-completion-total'],
    queryFn: () => userManagementService.list({ pageSize: 1, page: 1 }),
  });

  if (isLoading) return <SkeletonPage />;

  const users = data?.items ?? [];
  const totalUsers = allUsers?.total ?? 0;
  const completeCount = Math.max(totalUsers - users.length, 0);
  // Guard the divide: a tenant with no users is vacuously complete, not NaN%.
  const completionPct = totalUsers === 0 ? 100 : Math.round((completeCount / totalUsers) * 100);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Incomplete Identity"
        description="Employees missing a username, mobile number, or employee code. These accounts were created before those fields became mandatory."
      />

      {/* Completion progress. Shown in both states — at 100% it is the clearest
          confirmation that the backfill is genuinely finished. */}
      <div className="rounded-lg border border-border bg-card p-4">
        <div className="flex items-baseline justify-between gap-3">
          <span className="text-sm font-medium text-foreground">Identity completion</span>
          <span className="text-sm tabular-nums text-muted-foreground">
            {completeCount} of {totalUsers} complete ({completionPct}%)
          </span>
        </div>
        <div
          className="mt-2 h-2 w-full overflow-hidden rounded-full bg-muted"
          role="progressbar"
          aria-valuenow={completionPct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Employees with a complete identity"
        >
          <div
            className={`h-full rounded-full transition-all ${
              completionPct === 100 ? 'bg-emerald-500' : 'bg-amber-500'
            }`}
            style={{ width: `${completionPct}%` }}
          />
        </div>
      </div>

      {users.length === 0 ? (
        <EmptyState
          icon={faCircleCheck}
          title="Every employee has a complete identity"
          description="No accounts are missing a username, mobile number, or employee code."
        />
      ) : (
        <>
          <div className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 dark:border-amber-700/50 dark:bg-amber-900/15">
            <FontAwesomeIcon
              icon={faTriangleExclamation}
              className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-700 dark:text-amber-400"
            />
            <p className="text-xs text-amber-800 dark:text-amber-300">
              {users.length} {users.length === 1 ? 'employee is' : 'employees are'} missing part of
              their identity. They can still sign in, but saving any change to their profile will
              require the missing details first.
            </p>
          </div>

          {/* Wide content scrolls inside its own container so the page body never
              scrolls horizontally on small screens. */}
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="w-full min-w-[640px] text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Employee</th>
                  <th className="px-4 py-3 font-medium">Username</th>
                  <th className="px-4 py-3 font-medium">Mobile</th>
                  <th className="px-4 py-3 font-medium">Employee Code</th>
                  <th className="px-4 py-3 font-medium">Missing</th>
                  <th className="px-4 py-3 font-medium text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => {
                  const missing = missingFields(u);
                  return (
                    <tr key={u.id} className="border-b border-border last:border-0">
                      <td className="px-4 py-3 text-foreground">
                        {`${u.firstName} ${u.lastName}`.trim()}
                      </td>
                      <Cell value={u.username} />
                      <Cell value={u.phone} />
                      <Cell value={u.employeeCode} />
                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1">
                          {missing.map((m) => (
                            <span
                              key={m}
                              className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
                            >
                              {m}
                            </span>
                          ))}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/organization/users/${u.id}`}>
                          <Button type="button" variant="secondary" className="min-h-[36px]">
                            Complete
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Monospace cell that marks an absent value rather than rendering blank.
 *
 * Whitespace counts as absent, matching missingFields(). Without the trim these
 * two disagreed: a value of "   " was flagged as missing in the badge column
 * while this cell rendered it as though it were set.
 */
function Cell({ value }: { value: string | null }) {
  return (
    <td className="px-4 py-3">
      {value?.trim() ? (
        <span className="font-mono text-foreground">{value}</span>
      ) : (
        <span className="text-xs italic text-muted-foreground">Not set</span>
      )}
    </td>
  );
}
